import { ProcurementPlanSubmitErrorCategory } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

import type { InterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'

/**
 * 送信失敗 (SEND_FAILED) の原因 4 区分 (plan-common AC-PLC-020-2 / 050 / 052 / 053)。
 *
 * 原因の分類は backend が担い、送信失敗の `error_category` を
 * CREDENTIAL_NOT_CONFIGURED / CREDENTIAL_INVALID / OCCTO_CALL_LIMIT_REACHED /
 * OCCTO_UNREACHABLE のどれかに正規化して返す。frontend は category を表示用の
 * 区分へ写すだけで、再分類はしない。
 */
export type SendFailedCause =
  | 'occtoUnreachable'
  | 'occtoCallLimitReached'
  | 'credentialNotConfigured'
  | 'credentialInvalid'

/**
 * 送信失敗の category → 原因区分。全 category を列挙し、category が増えた時に
 * tsc が写像漏れを検出できるようにする。
 *
 * 送信失敗では backend が category を下の 4 値 (OCCTO_UNREACHABLE /
 * OCCTO_CALL_LIMIT_REACHED / CREDENTIAL_NOT_CONFIGURED / CREDENTIAL_INVALID) の
 * どれかに正規化して返す。残りの値への写像は、画面を壊さないための防御用で、
 * 業務上の分類ではない:
 * - UNSPECIFIED: BFF converter が未知の enum 値を受けた時の fallback
 * - それ以外: 送信失敗では backend が返さない (作成失敗・提出失敗・差戻しの category)
 * どちらも、原因を特定できない送信失敗に一番近い通信障害の文言で出す。
 */
const SEND_FAILED_CAUSE_BY_CATEGORY: Record<
  ProcurementPlanSubmitErrorCategory,
  SendFailedCause
> = {
  [ProcurementPlanSubmitErrorCategory.OCCTO_UNREACHABLE]: 'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.OCCTO_CALL_LIMIT_REACHED]:
    'occtoCallLimitReached',
  [ProcurementPlanSubmitErrorCategory.CREDENTIAL_NOT_CONFIGURED]:
    'credentialNotConfigured',
  [ProcurementPlanSubmitErrorCategory.CREDENTIAL_INVALID]: 'credentialInvalid',
  // 以下は防御用の fallback (上の docstring 参照)
  [ProcurementPlanSubmitErrorCategory.UNSPECIFIED]: 'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.PLAN_MASTER_MISSING]: 'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.DEMAND_FORECAST_UNCONFIRMED]:
    'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.SOURCE_DATA_INCOMPLETE]:
    'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.RETRY_EXHAUSTED]: 'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.OTHER]: 'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.GATE_CLOSED_ALL_KOMA]: 'occtoUnreachable',
  [ProcurementPlanSubmitErrorCategory.BREAKDOWN_LIMIT_EXCEEDED]:
    'occtoUnreachable',
}

export function resolveSendFailedCause(
  category: ProcurementPlanSubmitErrorCategory,
): SendFailedCause {
  return SEND_FAILED_CAUSE_BY_CATEGORY[category]
}

/**
 * 原因の短い区別 (提出管理カード AC-PSM-053 の「通信障害 / 回数上限 / 認証情報」、
 * 計画詳細の誘導メッセージの見出し)。未登録と拒否はどちらも「認証情報」
 * (プロト ebalance-prototype #468 `SEND_FAIL_CAUSES.short`)。
 */
export const SEND_FAILED_CAUSE_SHORT_KEY: Record<SendFailedCause, string> = {
  occtoUnreachable:
    'page:planSubmission.sendFailedCause.short.occtoUnreachable',
  occtoCallLimitReached:
    'page:planSubmission.sendFailedCause.short.occtoCallLimitReached',
  credentialNotConfigured:
    'page:planSubmission.sendFailedCause.short.credential',
  credentialInvalid: 'page:planSubmission.sendFailedCause.short.credential',
}

type CauseTextKeys = Record<
  Exclude<SendFailedCause, 'credentialInvalid'>,
  string
> & {
  credentialInvalid: string
  credentialInvalidWithoutCodes: string
}

function resolveCauseText(
  keys: CauseTextKeys,
  cause: SendFailedCause,
  rejectionCodes: readonly string[],
): InterpolatedMessage {
  if (cause !== 'credentialInvalid') {
    return { key: keys[cause] }
  }
  // backend は拒否の時に応答コードを必ず入れるが、空で届いた時に
  // 「応答コード: 」 の空の括弧を出さないよう、コードなしの文に切り替える。
  return rejectionCodes.length > 0
    ? { key: keys.credentialInvalid, rejectionCodes }
    : { key: keys.credentialInvalidWithoutCodes }
}

const CARD_KEYS: CauseTextKeys = {
  occtoUnreachable: 'page:planSubmission.sendFailedCause.card.occtoUnreachable',
  occtoCallLimitReached:
    'page:planSubmission.sendFailedCause.card.occtoCallLimitReached',
  credentialNotConfigured:
    'page:planSubmission.sendFailedCause.card.credentialNotConfigured',
  credentialInvalid:
    'page:planSubmission.sendFailedCause.card.credentialInvalid',
  credentialInvalidWithoutCodes:
    'page:planSubmission.sendFailedCause.card.credentialInvalidWithoutCodes',
}

const GUIDANCE_KEYS: CauseTextKeys = {
  occtoUnreachable:
    'page:planSubmission.sendFailedCause.guidance.occtoUnreachable',
  occtoCallLimitReached:
    'page:planSubmission.sendFailedCause.guidance.occtoCallLimitReached',
  credentialNotConfigured:
    'page:planSubmission.sendFailedCause.guidance.credentialNotConfigured',
  credentialInvalid:
    'page:planSubmission.sendFailedCause.guidance.credentialInvalid',
  credentialInvalidWithoutCodes:
    'page:planSubmission.sendFailedCause.guidance.credentialInvalidWithoutCodes',
}

/** 提出管理カードの 1 文 (プロト #468 `SEND_FAIL_CAUSES.card`。AC-PSM-053)。 */
export function resolveSendFailedCardText(
  cause: SendFailedCause,
  rejectionCodes: readonly string[],
): InterpolatedMessage {
  return resolveCauseText(CARD_KEYS, cause, rejectionCodes)
}

/** 計画詳細の誘導メッセージの本文 (AC-PLC-020-2 の文言そのまま)。 */
export function resolveSendFailedGuidanceText(
  cause: SendFailedCause,
  rejectionCodes: readonly string[],
): InterpolatedMessage {
  return resolveCauseText(GUIDANCE_KEYS, cause, rejectionCodes)
}
